// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import reorder from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/Reorder";
import translate from "@insite/client-framework/Translate";
import { OrderModel, OrderSettingsModel } from "@insite/client-framework/Types/ApiModels";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";

import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import IconReorder from "@plumberssupply/XDL/Icons/IconReorder";
import { color, mergeWithCss, xScreen } from "@plumberssupply/XDL/Utilities";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
    isReordering: state.pages.orderHistory.isReordering,
    showAddToCartConfirmationDialog: getSettingsCollection(state).productSettings.showAddToCartConfirmationDialog,
});

const mapDispatchToProps = {
    reorder,
};

interface OwnProps {
    order: OrderModel;
    orderSettings: OrderSettingsModel;
    extendedStyles?: OrderSummaryCardStyles;
    // XNG-Change
    isEmployee?: boolean;
}

type Props = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps &
    HasToasterContext;

export interface OrderSummaryCardStyles {
    orderSummaryCardGridItem?: GridItemProps;
    container?: GridContainerProps;
    pageTypeLinkGridItem?: GridItemProps;
    orderNumberGridItem?: GridItemProps;
    orderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    webOrderNumberGridItem?: GridItemProps;
    webOrderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    totalGridItem?: GridItemProps;
    totalHeadingAndText?: SmallHeadingAndTextStyles;
    dataTableCells?: DataTableCellBaseProps;
    mobileTitle?: TypographyPresentationProps;
    dataCell?: DataTableCellProps;
    companyNameCell?: DataTableCellProps;
    reorderButtonSpinner?: LoadingSpinnerProps;
    reorderLink?: LinkPresentationProps;
    customerPOCell?: DataTableCellProps;
    reorderCell?: DataTableCellProps;
    mobileSeparator?: InjectableCss;
}

export const orderSummaryCardStyles: OrderSummaryCardStyles = {
    orderSummaryCardGridItem: { width: 12 },
    container: { gap: 5 },
    pageTypeLinkGridItem: { width: 12 },
    orderNumberGridItem: { width: 6 },
    statusGridItem: { width: 6 },
    webOrderNumberGridItem: { width: 6 },
    totalGridItem: { width: 6 },
    reorderLink: {
        icon: {
            iconProps: {
                src: IconReorder,
                size: 14,
                css: css`
                    opacity: 0.5;
                `,
            },
        },
        typographyProps: {
            size: 14,
            weight: 800,
            lineHeight: "22px",
        },
    },
    mobileTitle: {
        size: 14,
        lineHeight: "22px",
        weight: 800,
        css: css`
            display: none;
            ${xScreen("lte", "md")} {
                display: inline-block;
                width: 50%;
            }
        `,
    },
    dataCell: {
        css: css`
            font-size: 14px;
            line-height: 22px;
            ${xScreen("lte", "md")} {
                display: flex;
                border: none;
                padding: 16px;
                height: auto;

                &:not(:last-of-type){
                    padding-bottom: 4px;
                }
                
                & > span {
                    display: inline-block;
                    width: 50%;
                }
            }
        `,
    },
    companyNameCell: {
        typographyProps: {
            ellipsis: true,
        },
        css: css`
            max-width: 320px;
            ${xScreen("gt", "md")} {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            ${xScreen("lte", "md")} {
                height: 80px;
                max-width: none;
                white-space: pre-wrap;
                overflow: hidden;
            }
        `,
    },
    customerPOCell: {
        css: css`
            span {
                font-size: 14px;
            }
            ${xScreen("lte", "md")} {
                a {
                    max-width: 100%;
                    span {
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                    }
                }
            }
        `,
    },
    reorderCell: {
        css: css`
            border-left: 1px solid ${color("common.ui8")};

            ${xScreen("lte", "md")} {
                border-left: none;
                height: inherit;
                border-bottom: 1px solid ${color("common.ui8")};
            }
        `,
    },
    mobileSeparator: {
        css: css`
            display: none;
            border-bottom: 1px solid ${color("common.ui8")};
            margin: 16px 16px 4px;

            ${xScreen("lte", "md")} {
                display: block;
            }
        `,
    },
    reorderButtonSpinner: {
        css: css`
            margin: auto;
            display: block;
        `,
    }
};

const OrderSummaryCard: FC<Props> = ({
    language,
    isReordering,
    reorder,
    showAddToCartConfirmationDialog,
    order,
    toaster,
    orderSettings,
    extendedStyles,
    isEmployee,
}) => {
    const [styles] = React.useState(() => mergeToNew(orderSummaryCardStyles, extendedStyles));

    const orderDateDisplay = getLocalizedDateTime({
        dateTime: order.orderDate,
        language,
        options: {
            year: "2-digit",
            month: "numeric",
            day: "numeric",
        },
    });
    const orderNumber = order.erpOrderNumber || order.webOrderNumber;

    const onClickReorder = (event: any) => {
        // XNG-Changes: this being used to prevent row click function from being trigger
        event.stopPropagation();

        reorder({
            orderNumber,
            onSuccess: () => {
                if (!showAddToCartConfirmationDialog) {
                    return;
                }
                toaster.addToast({
                    body: (
                        <>
                            <OrderDetailPageTypeLink
                                title={order.webOrderNumber}
                                orderNumber={order.erpOrderNumber || order.webOrderNumber}
                            />
                            <StyledWrapper as="span">&nbsp;{translate("Added to Cart")}</StyledWrapper>
                        </>
                    ),
                    messageType: "success",
                    timeoutLength: 6000,
                });
            },
        });
    };
    const shipToPickUp = `${order.stCompanyName} ${order.stAddress1} ${order.stAddress2} ${order.shipToCity} ${order.shipToState}`;
    return (
        <>
            <DataTableCell {...styles.dataCell}>
                <Typography {...styles.mobileTitle}>{translate("Date")}</Typography>
                <Typography>{orderDateDisplay}</Typography>
            </DataTableCell>
            <DataTableCell {...mergeWithCss(styles.dataCell, styles.customerPOCell)}>
                <Typography {...styles.mobileTitle}>{translate("PO #")}</Typography>
                <Typography><OrderDetailPageTypeLink title={order.customerPO || "--"} orderNumber={orderNumber} /></Typography>
            </DataTableCell>
            <DataTableCell {...styles.dataCell}>
                <Typography {...styles.mobileTitle}>{translate("Order #")}</Typography>
                {order.erpOrderNumber && (<Typography>{order.erpOrderNumber || "--"}</Typography>)}
                {!order.erpOrderNumber && order.webOrderNumber && (<Typography>{order.webOrderNumber || "--"}</Typography>)}
            </DataTableCell>
            <DataTableCell {...mergeWithCss(styles.dataCell, styles.companyNameCell)}>
                <Typography {...styles.mobileTitle}>{translate("Ship To / Pick Up")}</Typography>
                <Typography>{`${shipToPickUp || "--"}`}</Typography>
            </DataTableCell>
            <DataTableCell {...styles.dataCell}>
                <Typography {...styles.mobileTitle}>{translate("Order Status")}</Typography>
                <Typography>{`${order.statusDisplay || "--"}`}</Typography>
            </DataTableCell>
            {!isEmployee && (<DataTableCell {...styles.dataCell}>
                <Typography {...styles.mobileTitle}>{translate("Amount")}</Typography>
                <Typography>{`${order.orderGrandTotalDisplay || "--"}`}</Typography>
            </DataTableCell>)}
            <StyledWrapper {...styles.mobileSeparator}></StyledWrapper>
            <DataTableCell {...mergeWithCss(styles.dataCell, styles.reorderCell)} className="reorderCell">
                <Typography {...styles.mobileTitle}>{translate("Actions")}</Typography>
                {isReordering[orderNumber] && <LoadingSpinner {...styles.reorderButtonSpinner} size={20} />}
                {!isReordering[orderNumber] && (
                    <Link
                        disabled={Object.keys(isReordering).length > 0}
                        {...styles.reorderLink}
                        onClick={onClickReorder}
                    >
                        {translate("Reorder")}
                    </Link>
                )}
            </DataTableCell>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withToaster(OrderSummaryCard));
